import { useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import { Label } from "@volca/ui/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@volca/ui/components/ui/radio-group";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  useImpersonationBusinessOptions,
  useImpersonationTenantOptions,
  useImpersonationUserOptions,
} from "@/core/impersonation/impersonation-options";
import {
  getImpersonationUserOptions,
  type ImpersonationParentType,
} from "@/nav/impersonation-modal-utils";
import type { ImpersonationFormValues } from "./types";

export const ImpersonationFields = ({ open }: { open: boolean }) => {
  const { values, setValues, setFieldValue, isSubmitting } =
    useFormikContext<ImpersonationFormValues>();
  const { parentType, selectedParentId } = values;
  const parentInputRef = useRef<HTMLInputElement>(null);
  const initialFocusHandled = useRef(false);

  const tenantsQuery = useImpersonationTenantOptions(
    open && parentType === "tenant",
  );
  const businessesQuery = useImpersonationBusinessOptions(
    open && parentType === "business",
  );
  const userQuery = useImpersonationUserOptions(
    parentType,
    selectedParentId,
    open,
  );

  useEffect(() => {
    if (!open) {
      initialFocusHandled.current = false;
      return;
    }

    if (parentType !== "business") {
      // Switching to Tenant cancels the pending business focus for this opening.
      initialFocusHandled.current = true;
      return;
    }

    if (
      initialFocusHandled.current ||
      !businessesQuery.isSuccess ||
      isSubmitting
    ) {
      return;
    }

    const input = parentInputRef.current;
    if (input && !input.disabled) {
      input.focus();
      initialFocusHandled.current = true;
    }
  }, [open, parentType, businessesQuery.isSuccess, isSubmitting]);

  const handleParentTypeChange = (value: ImpersonationParentType) => {
    void setValues(
      {
        ...values,
        parentType: value,
        selectedParentId: "",
        selectedUserId: "",
        parentSearchValue: "",
        userSearchValue: "",
      },
      true,
    );
  };

  const handleParentChange = (value: string) => {
    void setValues(
      {
        ...values,
        selectedParentId: value,
        selectedUserId: "",
        userSearchValue: "",
      },
      true,
    );
  };

  const handleUserChange = (value: string) => {
    void setFieldValue("selectedUserId", value, true);
  };

  const parentQuery = parentType === "tenant" ? tenantsQuery : businessesQuery;
  const userOptions = getImpersonationUserOptions(userQuery.data, parentType);

  return (
    <Stack align="start" className="w-full" gap={6}>
      {/* Parent Type Selection */}
      <Stack className="w-full" align="start" gap={4}>
        <Label>Select Type</Label>
        <RadioGroup
          value={values.parentType}
          onValueChange={handleParentTypeChange}
          className="flex gap-4"
          disabled={isSubmitting}
        >
          <Stack direction="row" align="center" gap={2}>
            <RadioGroupItem value="business" id="business" />
            <Label htmlFor="business" className="cursor-pointer">
              Business
            </Label>
          </Stack>
          <Stack direction="row" align="center" gap={2}>
            <RadioGroupItem value="tenant" id="tenant" />
            <Label htmlFor="tenant" className="cursor-pointer">
              Tenant
            </Label>
          </Stack>
        </RadioGroup>
      </Stack>

      {/* Parent Selection */}
      <AutoComplete
        ref={parentInputRef}
        ariaLabel={parentType === "tenant" ? "Tenant" : "Business"}
        selectedValue={values.selectedParentId}
        onSelectedValueChange={handleParentChange}
        searchValue={values.parentSearchValue}
        onSearchValueChange={(value) =>
          setFieldValue("parentSearchValue", value, false)
        }
        items={
          parentQuery.data?.map((p) => ({ value: p.id, label: p.name })) || []
        }
        placeholder={`Search ${values.parentType === "tenant" ? "tenants" : "businesses"}...`}
        label={values.parentType === "tenant" ? "Tenant" : "Business"}
        fullWidth
        isLoading={parentQuery.isLoading}
        disabled={isSubmitting || parentQuery.isLoading || parentQuery.isError}
      />

      {parentQuery.isLoading && (
        <Text variant="caption" tone="muted" role="status">
          Loading {parentType === "tenant" ? "tenants" : "businesses"}...
        </Text>
      )}

      {parentQuery.isError && (
        <Stack className="w-full" align="start" gap={1} role="alert">
          <Text variant="caption" tone="muted">
            Could not load {parentType === "tenant" ? "tenants" : "businesses"}.
          </Text>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={parentQuery.isFetching}
            aria-busy={parentQuery.isFetching || undefined}
            onClick={() => void parentQuery.refetch()}
          >
            {parentQuery.isFetching && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Try again
          </Button>
        </Stack>
      )}

      {/* User Selection */}
      <AutoComplete
        ariaLabel="User"
        emptyMessage="No eligible users."
        selectedValue={values.selectedUserId}
        onSelectedValueChange={handleUserChange}
        searchValue={values.userSearchValue}
        onSearchValueChange={(value) =>
          setFieldValue("userSearchValue", value, false)
        }
        items={userOptions}
        placeholder={
          !values.selectedParentId ? "Select a parent first" : "Search users..."
        }
        label="User"
        fullWidth
        isLoading={userQuery.isLoading}
        disabled={
          isSubmitting ||
          !selectedParentId ||
          userQuery.isLoading ||
          userQuery.isError
        }
      />
      {selectedParentId && userQuery.isLoading && (
        <Text variant="caption" tone="muted" role="status">
          Loading users...
        </Text>
      )}
      {selectedParentId && userQuery.isError && (
        <Stack className="w-full" align="start" gap={1} role="alert">
          <Text variant="caption" tone="muted">
            Could not load users.
          </Text>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={userQuery.isFetching}
            aria-busy={userQuery.isFetching || undefined}
            onClick={() => void userQuery.refetch()}
          >
            {userQuery.isFetching && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Try again
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
